import * as THREE from 'three';

/*
 * The armory's effects (2026-10-25): what rare gear does in a fight, and the offerings' braziers by the
 * keep. Like the cleric's light (holy.ts): textures are drawn once on canvases (white on clear, coloured
 * by their material), meshes are pooled, `begin()`/`end()` wrap one sim tick of placing what lasts
 * (a hero's guard, fury, banner) and `frame()` animates every animation frame. Short bursts (a cleave's
 * crescent, an execution's cross, an echo's clock) are spawned from battle events and live on real time.
 */

const canvas = (w: number, h = w): [HTMLCanvasElement, CanvasRenderingContext2D] => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
};

const tex = (c: HTMLCanvasElement): THREE.Texture => {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

/** Every texture and shape made on first use below: a fight makes them all before it starts (warmGearFx). */
const LAZY: (() => unknown)[] = [];
const once = <T>(make: () => T): (() => T) => {
  let v: T | null = null;
  const get = (): T => (v ??= make());
  LAZY.push(get);
  return get;
};

/**
 * Makes the gear' effects' textures now. Each is painted pixel by pixel the first time it is used; that first
 * time used to be in the middle of a fight, 170 ms on a slow phone when a whirl and a rare blade went off together.
 */
export const warmGearFx = (): void => {
  for (const f of LAZY) f();
};

/** Small seeded angular glyphs, for rune rings. */
const glyph = (g: CanvasRenderingContext2D, x: number, y: number, s: number, seed: number): void => {
  let k = seed * 2654435761;
  const r = (): number => {
    k = (Math.imul(k ^ (k >>> 13), 1274126177) + 0x6d2b79f5) >>> 0;
    return k / 4294967296;
  };
  g.beginPath();
  g.moveTo(x, y - s);
  g.lineTo(x, y + s);
  for (let i = 0, n = 1 + Math.floor(r() * 3); i < n; i++) {
    const yy = y - s + r() * 2 * s;
    const d = (r() < 0.5 ? -1 : 1) * s * (0.4 + r() * 0.5);
    g.moveTo(x, yy);
    g.lineTo(x + d, yy + (r() - 0.5) * s);
  }
  g.stroke();
};

/**
 * A blade's crescent, seen from above: hot at its leading (outer) edge, fading inward, tapering to points
 * at both tips. The arc spans ±75° around +x from the centre of the left edge.
 */
const crescentTex = once(() => {
  const W = 512, H = 512;
  const [c, g] = canvas(W, H);
  const img = g.createImageData(W, H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const dx = x / W, dy = (y - H / 2) / (H / 2);
      const r = Math.hypot(dx, dy * 1.0);
      const th = Math.atan2(dy, dx);
      const span = (75 * Math.PI) / 180;
      const u = Math.abs(th) / span; // 0 middle, 1 tips
      if (u >= 1 || r > 1) continue;
      // The edge thins toward the tips; the hot rim sits just inside r = 1.
      const thick = 0.32 * (1 - u * u);
      const d = (1 - r) / Math.max(0.001, thick);
      if (d > 1) continue;
      const rim = Math.exp(-d * d * 9);
      const body = (1 - d) * 0.55;
      const a = Math.min(1, (rim + body) * (1 - Math.pow(u, 3)));
      const o = (y * W + x) * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.round(a * 255);
    }
  }
  g.putImageData(img, 0, 0);
  return tex(c);
});

/** Two crossing cuts, tapered at both ends, with a soft glow: the executioner's mark. */
const crossTex = once(() => {
  const S = 256, C = S / 2;
  const [c, g] = canvas(S);
  g.fillStyle = '#fff';
  g.shadowColor = '#fff';
  g.shadowBlur = 18;
  const cut = (a: number, len: number, w: number): void => {
    g.save();
    g.translate(C, C);
    g.rotate(a);
    g.beginPath();
    g.moveTo(-len, 0);
    g.quadraticCurveTo(0, -w, len, 0);
    g.quadraticCurveTo(0, w * 0.35, -len, 0);
    g.fill();
    g.restore();
  };
  cut(Math.PI / 4, 0.92 * C, 0.2 * C);
  cut(-Math.PI / 4, 0.86 * C, 0.17 * C);
  return tex(c);
});

/** The guard's ward on the ground: a double hexagon with runes along its sides. */
const hexTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 10;
  const hex = (r: number, w: number): void => {
    g.lineWidth = w;
    g.beginPath();
    for (let i = 0; i <= 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      const x = C + Math.cos(a) * r * C, y = C + Math.sin(a) * r * C;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  };
  hex(0.95, 8);
  hex(0.8, 3);
  hex(0.42, 3);
  g.lineWidth = 3;
  for (let side = 0; side < 6; side++) {
    const a0 = (side / 6) * Math.PI * 2 + Math.PI / 6, a1 = a0 + Math.PI / 3;
    for (let k = 1; k <= 3; k++) {
      const t = k / 4;
      const x = C + (Math.cos(a0) * (1 - t) + Math.cos(a1) * t) * 0.875 * C;
      const y = C + (Math.sin(a0) * (1 - t) + Math.sin(a1) * t) * 0.875 * C;
      g.save();
      g.translate(x, y);
      g.rotate((a0 + a1) / 2 + Math.PI / 2);
      glyph(g, 0, 0, 0.03 * C, side * 3 + k);
      g.restore();
    }
    // Spokes from the inner hexagon to the corners.
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(C + Math.cos(a0) * 0.42 * C, C + Math.sin(a0) * 0.42 * C);
    g.lineTo(C + Math.cos(a0) * 0.8 * C, C + Math.sin(a0) * 0.8 * C);
    g.stroke();
    g.lineWidth = 3;
  }
  return tex(c);
});

/** Fury on the ground: a ring of jagged teeth, like flames leaning outward. */
const rageTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.fillStyle = '#fff';
  g.strokeStyle = '#fff';
  g.shadowColor = '#fff';
  g.shadowBlur = 14;
  const n = 18;
  g.beginPath();
  for (let i = 0; i <= n * 2; i++) {
    const a = (i / (n * 2)) * Math.PI * 2;
    const r = i % 2 ? 0.97 : 0.72 + 0.05 * Math.sin(i * 1.7);
    const lean = i % 2 ? 0.08 : 0;
    const x = C + Math.cos(a + lean) * r * C, y = C + Math.sin(a + lean) * r * C;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.globalAlpha = 0.55;
  g.fill();
  g.globalAlpha = 1;
  g.lineWidth = 4;
  g.stroke();
  // Cut the middle out: only the crown of teeth and a thin inner ring stay.
  g.globalCompositeOperation = 'destination-out';
  g.beginPath();
  g.arc(C, C, 0.68 * C, 0, Math.PI * 2);
  g.fill();
  g.globalCompositeOperation = 'source-over';
  g.lineWidth = 3;
  g.beginPath();
  g.arc(C, C, 0.6 * C, 0, Math.PI * 2);
  g.stroke();
  return tex(c);
});

/** The echo's clock: twelve marks, three heavy ones, and the hand. */
const clockTex = once(() => {
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 12;
  g.lineWidth = 5;
  g.beginPath();
  g.arc(C, C, 0.93 * C, 0, Math.PI * 2);
  g.stroke();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const heavy = i % 3 === 0;
    g.lineWidth = heavy ? 7 : 3;
    g.beginPath();
    g.moveTo(C + Math.cos(a) * (heavy ? 0.66 : 0.76) * C, C + Math.sin(a) * (heavy ? 0.66 : 0.76) * C);
    g.lineTo(C + Math.cos(a) * 0.88 * C, C + Math.sin(a) * 0.88 * C);
    g.stroke();
  }
  // A swept quarter, brighter at the hand: the time given back.
  for (let i = 0; i < 30; i++) {
    g.globalAlpha = (i / 30) * 0.35;
    g.lineWidth = 0.58 * C;
    g.beginPath();
    g.arc(C, C, 0.3 * C, -Math.PI / 2 - ((30 - i) / 30) * (Math.PI / 2), -Math.PI / 2 - ((29 - i) / 30) * (Math.PI / 2));
    g.stroke();
  }
  g.globalAlpha = 1;
  g.lineWidth = 6;
  g.beginPath();
  g.moveTo(C, C);
  g.lineTo(C, C - 0.62 * C);
  g.stroke();
  g.beginPath();
  g.arc(C, C, 0.05 * C, 0, Math.PI * 2);
  g.stroke();
  return tex(c);
});

/** Frost: a six-armed ice star with side branches. */
const frostTex = once(() => {
  const S = 256, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 10;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    g.save();
    g.translate(C, C);
    g.rotate(a);
    g.lineWidth = 6;
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(0.9 * C, 0);
    g.stroke();
    g.lineWidth = 4;
    for (const t of [0.4, 0.62]) {
      g.beginPath();
      g.moveTo(t * C, 0);
      g.lineTo((t + 0.18) * C, -0.16 * C);
      g.moveTo(t * C, 0);
      g.lineTo((t + 0.18) * C, 0.16 * C);
      g.stroke();
    }
    g.restore();
  }
  return tex(c);
});

/** A soft round glow (the echo's wisp and its trail). */
const dotTex = once(() => {
  const S = 128, C = S / 2;
  const [c, g] = canvas(S);
  const gr = g.createRadialGradient(C, C, 0, C, C, C);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.25, 'rgba(255,255,255,0.8)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  return tex(c);
});

/** The banner's cloth: a swallow-tailed pennant in the dragon's red, a pale border and a flame sigil. */
const pennantTex = once(() => {
  const W = 256, H = 384;
  const [c, g] = canvas(W, H);
  const shape = (inset: number): void => {
    g.beginPath();
    g.moveTo(inset, inset);
    g.lineTo(W - inset, inset);
    g.lineTo(W - inset, H - inset);
    g.lineTo(W / 2, H * 0.78 - inset * 0.5);
    g.lineTo(inset, H - inset);
    g.closePath();
  };
  shape(0);
  const gr = g.createLinearGradient(0, 0, 0, H);
  gr.addColorStop(0, '#c4271b');
  gr.addColorStop(1, '#7a1a14');
  g.fillStyle = gr;
  g.fill();
  shape(14);
  g.lineWidth = 6;
  g.strokeStyle = '#ffb59e';
  g.stroke();
  // The sigil: a tongue of flame over a bar.
  g.fillStyle = '#ffe2d8';
  g.beginPath();
  const cx = W / 2, cy = H * 0.42;
  g.moveTo(cx, cy - 78);
  g.bezierCurveTo(cx + 44, cy - 20, cx + 40, cy + 30, cx, cy + 44);
  g.bezierCurveTo(cx - 40, cy + 30, cx - 36, cy - 4, cx - 10, cy - 30);
  g.bezierCurveTo(cx - 8, cy - 4, cx + 2, cy + 8, cx + 8, cy + 2);
  g.bezierCurveTo(cx + 14, cy - 20, cx + 6, cy - 50, cx, cy - 78);
  g.fill();
  g.fillRect(cx - 52, cy + 58, 104, 10);
  return tex(c);
});

/** The banner's reach on the ground: a dashed ring with a rune between dashes. */
const reachTex = once(() => {
  const S = 1024, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 8;
  const n = 24;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    g.lineWidth = 7;
    g.beginPath();
    g.arc(C, C, 0.965 * C, a, a + (Math.PI * 2) / n * 0.55);
    g.stroke();
    g.lineWidth = 3;
    const b = a + (Math.PI * 2) / n * 0.78;
    g.save();
    g.translate(C + Math.cos(b) * 0.965 * C, C + Math.sin(b) * 0.965 * C);
    g.rotate(b + Math.PI / 2);
    glyph(g, 0, 0, 0.018 * C, i + 40);
    g.restore();
  }
  return tex(c);
});

const additive = (map: THREE.Texture, color: string): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ map, color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true, opacity: 0 });

/** A flat decal on the ground (a unit circle's worth of plane; scale sets its radius). */
const flat = (mat: THREE.Material, order: number): THREE.Mesh => {
  const m = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  m.rotation.x = -Math.PI / 2;
  m.renderOrder = order;
  m.visible = false;
  return m;
};

// ---------- shaders ----------

/** Rising fire on an open cone: noisy tongues climbing, hot at the bottom, gone at the top. */
const FLAME_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const FLAME_FRAG = /* glsl */ `
uniform vec3 color;
uniform vec3 core;
uniform float opacity;
uniform float time;
uniform float seed;
varying vec2 vUv;
float h(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float n(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h(i), h(i + vec2(1.0, 0.0)), f.x), mix(h(i + vec2(0.0, 1.0)), h(i + vec2(1.0, 1.0)), f.x), f.y);
}
void main() {
  float y = vUv.y;
  vec2 q = vec2(vUv.x * 8.0, y * 2.2 - time * 2.6 + seed);
  float f = n(q) * 0.6 + n(q * 2.1 + 3.7) * 0.4;
  // Tongues: noise cut by a threshold that rises with height, so the fire breaks into licks at the top.
  float tongue = smoothstep(0.18, 0.6, f + (1.0 - y) * 0.6 - 0.2);
  float fade = pow(1.0 - y, 0.9) * smoothstep(0.0, 0.1, y + 0.02);
  float a = clamp(tongue * fade * opacity, 0.0, 1.0);
  vec3 c = mix(color, core, clamp(pow(1.0 - y, 3.0) * tongue, 0.0, 1.0)) * 0.85;
  gl_FragColor = vec4(c * a, a);
  #include <colorspace_fragment>
}`;
const flameMat = (color: string, core: string): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { color: { value: new THREE.Color(color) }, core: { value: new THREE.Color(core) }, opacity: { value: 0 }, time: { value: 0 }, seed: { value: Math.random() * 10 } },
    vertexShader: FLAME_VERT,
    fragmentShader: FLAME_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    side: THREE.DoubleSide, forceSinglePass: true,
  });

/** The guard's shell: a hexagonal prism, bright at its edges and rim, with light running up its faces. */
const SHELL_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
varying vec2 vUv;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = normalize(-mv.xyz);
  vUv = uv;
  gl_Position = projectionMatrix * mv;
}`;
const SHELL_FRAG = /* glsl */ `
uniform vec3 color;
uniform float opacity;
uniform float time;
varying vec3 vN;
varying vec3 vV;
varying vec2 vUv;
void main() {
  float rim = pow(1.0 - abs(dot(normalize(vN), normalize(vV))), 1.6);
  // Six faces around: bright seams at the corners.
  float u = fract(vUv.x * 6.0);
  float seam = smoothstep(0.08, 0.0, u) + smoothstep(0.92, 1.0, u);
  float scan = smoothstep(0.08, 0.0, abs(fract(vUv.y * 1.0 - time * 0.6) - 0.5) - 0.42);
  float top = smoothstep(0.82, 1.0, vUv.y);
  float a = (0.025 + rim * 0.22 + seam * 0.5 + scan * 0.16 + top * 0.18) * (1.0 - vUv.y * 0.45) * opacity;
  gl_FragColor = vec4(color * (0.9 + seam * 0.6), a);
  #include <colorspace_fragment>
}`;

/** The pennant waves from its pole: a travelling wave that grows toward the free edge. */
const CLOTH_VERT = /* glsl */ `
uniform float time;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec3 p = position;
  float k = uv.x;
  p.z += sin(uv.x * 5.0 - time * 5.0) * 0.07 * k + sin(uv.y * 3.0 + time * 3.0) * 0.02 * k;
  p.y += sin(uv.x * 4.0 - time * 5.0) * 0.015 * k;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}`;
const CLOTH_FRAG = /* glsl */ `
uniform sampler2D map;
uniform float opacity;
uniform float time;
varying vec2 vUv;
void main() {
  vec4 t = texture2D(map, vUv);
  if (t.a < 0.4) discard;
  float shade = 0.82 + 0.18 * sin(vUv.x * 5.0 - time * 5.0);
  gl_FragColor = vec4(t.rgb * shade, t.a * opacity);
  #include <colorspace_fragment>
}`;

/** The guard's shell: a hexagonal ward of light around the hero. */
const shellMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { color: { value: new THREE.Color('#6fb4ff') }, opacity: { value: 0 }, time: { value: 0 } },
    vertexShader: SHELL_VERT,
    fragmentShader: SHELL_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide, forceSinglePass: true,
  });

/** The war banner's cloth, waving. */
const clothMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { map: { value: pennantTex() }, opacity: { value: 0 }, time: { value: 0 } },
    vertexShader: CLOTH_VERT,
    fragmentShader: CLOTH_FRAG,
    transparent: true,
    side: THREE.DoubleSide, forceSinglePass: true,
  });

// ---------- pooled pieces ----------

interface Guard { g: THREE.Group; shell: THREE.Mesh; sigil: THREE.Mesh; shown: number; want: number }
interface Fury { g: THREE.Group; aura: THREE.Mesh; ring: THREE.Mesh; shown: number; want: number }
interface Banner { g: THREE.Group; cloth: THREE.Mesh; reach: THREE.Mesh; shown: number; want: number; reachR: number }
interface Brazier { g: THREE.Group; flame: THREE.Mesh; inner: THREE.Mesh }

type Burst = { mesh: THREE.Mesh | THREE.Sprite; t0: number; life: number; step: (k: number, m: THREE.Mesh | THREE.Sprite) => void; free: () => void };

export class GearFxLayer {
  private readonly keep: THREE.Group;
  /** The view's shaders are linked: the fire's keeper can go. */
  set primed(v: boolean) {
    this.keep.visible = !v;
  }
  readonly group = new THREE.Group();
  private guards: Guard[] = [];
  private guardsUsed = 0;
  private furies: Fury[] = [];
  private furiesUsed = 0;
  private banners: Banner[] = [];
  private bannersUsed = 0;
  private bursts: Burst[] = [];
  private braziers: Brazier[] = [];
  /** Spare meshes for the bursts, by kind. */
  private spare = new Map<string, (THREE.Mesh | THREE.Sprite)[]>();
  private hexGeo = new THREE.CylinderGeometry(0.62, 0.62, 1.25, 6, 1, true);
  private coneGeo = new THREE.CylinderGeometry(0.18, 0.5, 1.5, 24, 1, true);
  private wisps: { sprite: THREE.Sprite; from: THREE.Vector3; to: THREE.Vector3; t0: number; life: number; done: () => void }[] = [];
  quality: 'low' | 'high' = 'high';

  constructor() {
    // Far under the ground until the fight's compile ahead is done (it compiles only what is visible): a fire's
    // shader (braziers lit mid-Marea, a rare gear's burning aura) compiled on the spot otherwise. The guard's shell
    // and its sigil on the ground, and the banner's cloth too: made only when first raised, ~12 s into a fight, their shaders stalled a phone
    // a tenth of a second (client-perf, 2026-10-31).
    this.keep = new THREE.Group();
    this.keep.position.set(0, -60, 0);
    this.keep.scale.setScalar(0.0001);
    for (const mat of [flameMat('#ff4a1a', '#ffd0a0'), shellMat(), clothMat(), additive(hexTex(), '#cfe8ff')]) {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.2, 0.8, 6, 1, true), mat);
      m.frustumCulled = false;
      this.keep.add(m);
    }
    this.group.add(this.keep);
    this.group.renderOrder = 10;
  }

  private clock = (): number => performance.now() / 1000;

  /** Starts a sim tick: what lasts is placed again below, the rest fades. */
  begin(): void {
    this.guardsUsed = this.furiesUsed = this.bannersUsed = 0;
  }

  /** A hero under its guard: a hexagonal ward on the ground and a shell around it; `strength` 0..1. */
  guard(p: THREE.Vector3, strength: number): void {
    const w = this.guards[this.guardsUsed++] ?? this.makeGuard();
    w.g.position.copy(p);
    w.want = Math.max(0.25, Math.min(1, strength));
  }

  /** A hero in a fury: flames climb it and a crown of red teeth turns under it. */
  fury(p: THREE.Vector3): void {
    const f = this.furies[this.furiesUsed++] ?? this.makeFury();
    f.g.position.copy(p);
    f.want = 1;
  }

  /** A hero carrying a banner: the pennant over its shoulder, and its reach on the ground (world radius). */
  banner(p: THREE.Vector3, facing: number, reach: number, mine: boolean): void {
    const b = this.banners[this.bannersUsed++] ?? this.makeBanner();
    b.g.position.copy(p);
    b.g.rotation.y = facing;
    b.reachR = reach;
    b.want = mine ? 1 : 0.55;
  }

  end(): void {
    for (let i = this.guardsUsed; i < this.guards.length; i++) (this.guards[i] as Guard).want = 0;
    for (let i = this.furiesUsed; i < this.furies.length; i++) (this.furies[i] as Fury).want = 0;
    for (let i = this.bannersUsed; i < this.banners.length; i++) (this.banners[i] as Banner).want = 0;
  }

  // ---------- bursts ----------

  private take<T extends THREE.Mesh | THREE.Sprite>(kind: string, make: () => T): T {
    const list = this.spare.get(kind);
    const m = (list?.pop() as T | undefined) ?? make();
    m.visible = true;
    if (!m.parent) this.group.add(m);
    return m;
  }

  private give(kind: string, m: THREE.Mesh | THREE.Sprite): void {
    m.visible = false;
    // Out of the scene while spare: three recomputes the matrix of every object under the scene each frame, hidden
    // or not (~700 spare bursts after a big wave, the most of the scene's matrix work on a phone, 2026-11-01).
    this.group.remove(m);
    const list = this.spare.get(kind) ?? [];
    list.push(m);
    this.spare.set(kind, list);
  }

  private burst(kind: string, make: () => THREE.Mesh | THREE.Sprite, life: number, step: Burst['step']): void {
    const mesh = this.take(kind, make);
    const b: Burst = { mesh, t0: this.clock(), life, step, free: () => this.give(kind, mesh) };
    step(0, mesh);
    this.bursts.push(b);
  }

  /**
   * The cleave: a crescent swept from the hero through its target, wide as the cut reaches (world units),
   * low at the waist; it opens and sweeps past in a third of a second.
   */
  cleave(hero: THREE.Vector3, target: THREE.Vector3, reach: number): void {
    const dir = Math.atan2(target.x - hero.x, target.z - hero.z);
    const r = hero.distanceTo(target) + reach;
    const y = (hero.y + target.y) / 2 + 0.5;
    for (const [color, scale, life, opa] of [['#ff8a6e', 1, 0.42, 0.8], ['#ffffff', 0.94, 0.26, 1]] as const) {
      this.burst(
        `crescent:${color}`,
        () => {
          // Spans x 0..1 from the hero (the texture's arc is centred on its left edge).
          const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 2), additive(crescentTex(), color));
          m.geometry.translate(0.5, 0, 0);
          m.rotation.order = 'YXZ';
          m.renderOrder = 12;
          return m;
        },
        life,
        (k, m) => {
          m.position.set(hero.x, y, hero.z);
          // Lying almost flat with its arc toward the target (yaw dir − 90° turns +x onto it), swinging
          // from one side to the other as it goes.
          m.rotation.set(-Math.PI / 2 + 0.18, dir - Math.PI / 2 + (k - 0.5) * 0.9, 0);
          const s = r * scale * (0.75 + 0.3 * Math.sqrt(k));
          m.scale.set(s, s, 1);
          (m.material as THREE.MeshBasicMaterial).opacity = opa * (k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85);
        },
      );
    }
  }

  /** A short bright cut where the cleave carried into another nightmare. */
  cut(p: THREE.Vector3, color = '#ffe2d8', size = 0.55, life = 0.22): void {
    this.burst(
      `cut:${color}`,
      () => {
        const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: crossTex(), color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
        s.renderOrder = 13;
        return s;
      },
      life,
      (k, m) => {
        m.position.set(p.x, p.y + 0.5, p.z);
        const s = size * (0.7 + 0.5 * Math.sqrt(k));
        m.scale.set(s, s, 1);
        (m.material as THREE.SpriteMaterial).rotation = 0.3 + k * 0.3;
        (m.material as THREE.SpriteMaterial).opacity = 1 - k * k;
      },
    );
  }

  /** The executioner's mark: a large crimson cross that bursts on the target and lingers a moment. */
  execute(p: THREE.Vector3): void {
    this.cut(p, '#ff2a1a', 1.5, 0.6);
    this.cut(p, '#fff0ea', 1.05, 0.28);
  }

  /** Frost: an ice star opens on the ground under the target and melts away. */
  frost(p: THREE.Vector3): void {
    this.burst(
      'frost',
      () => {
        const m = flat(additive(frostTex(), '#bfe8ff'), 6);
        return m;
      },
      0.55,
      (k, m) => {
        m.position.set(p.x, p.y + 0.04, p.z);
        const s = 0.35 + 0.45 * Math.sqrt(k);
        m.scale.set(s, s, s);
        m.rotation.z = k * 0.8;
        (m.material as THREE.MeshBasicMaterial).opacity = 0.95 * (1 - k);
      },
    );
  }

  /**
   * Echo: a cold wisp flies from the fallen nightmare to the hero along a low arc, and when it arrives
   * a clock opens under the hero and spins backward: time given back to its skills.
   */
  echo(from: THREE.Vector3, to: () => THREE.Vector3 | null): void {
    const sprite = this.take('wisp', () => {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: dotTex(), color: '#bfefff', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
      s.renderOrder = 14;
      return s;
    });
    const end = to() ?? from.clone();
    this.wisps.push({
      sprite,
      from: from.clone().setY(from.y + 0.6),
      to: end.clone().setY(end.y + 0.8),
      t0: this.clock(),
      life: 0.42,
      done: () => {
        this.give('wisp', sprite);
        const at = to() ?? end;
        this.clockRing(at);
      },
    });
  }

  private clockRing(p: THREE.Vector3): void {
    this.burst(
      'clock',
      () => flat(additive(clockTex(), '#8fdcff'), 7),
      0.7,
      (k, m) => {
        m.position.set(p.x, p.y + 0.05, p.z);
        const s = 0.95 - 0.25 * k;
        m.scale.set(s, s, s);
        // Counter-clockwise, fast at first: the hand turns back.
        m.rotation.z = 2.4 * (1 - (1 - k) * (1 - k));
        (m.material as THREE.MeshBasicMaterial).opacity = k < 0.12 ? k / 0.12 : 1 - (k - 0.12) / 0.88;
      },
    );
  }

  /** The guard rises: its hexagon flashes out from the hero. */
  guardRise(p: THREE.Vector3): void {
    this.burst(
      'hexflash',
      () => flat(additive(hexTex(), '#e6f3ff'), 7),
      0.45,
      (k, m) => {
        m.position.set(p.x, p.y + 0.06, p.z);
        const s = 0.5 + 0.5 * Math.sqrt(k);
        m.scale.set(s, s, s);
        (m.material as THREE.MeshBasicMaterial).opacity = 1 - k;
      },
    );
  }

  /** The guard breaks: the hexagon bursts outward and fades. */
  guardBreak(p: THREE.Vector3): void {
    this.burst(
      'hexbreak',
      () => flat(additive(hexTex(), '#9fd0ff'), 7),
      0.4,
      (k, m) => {
        m.position.set(p.x, p.y + 0.06, p.z);
        const s = 0.75 + 0.8 * k;
        m.scale.set(s, s, s);
        m.rotation.z = k * 0.6;
        (m.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - k);
      },
    );
  }

  // ---------- the offerings' braziers ----------

  /** One brazier per lit offering, at the given spots: stone foot, iron bowl, fire in the offering's colour. */
  setBraziers(spots: { p: THREE.Vector3; color: string; core: string }[]): void {
    for (const b of this.braziers) {
      this.group.remove(b.g);
      b.g.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
    }
    this.braziers = spots.map((s) => this.makeBrazier(s.p, s.color, s.core));
  }

  private makeBrazier(p: THREE.Vector3, color: string, core: string): Brazier {
    const g = new THREE.Group();
    const stone = new THREE.MeshStandardMaterial({ color: '#8d877e', roughness: 0.95, flatShading: true });
    const iron = new THREE.MeshStandardMaterial({ color: '#2b2e35', roughness: 0.55, metalness: 0.6, flatShading: true });
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.26, 0.12, 6), stone);
    foot.position.y = 0.06;
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 0.32, 6), stone);
    shaft.position.y = 0.28;
    // The bowl: a turned iron dish with a lip.
    const prof = [new THREE.Vector2(0.02, 0), new THREE.Vector2(0.14, 0.02), new THREE.Vector2(0.22, 0.09), new THREE.Vector2(0.25, 0.15), new THREE.Vector2(0.23, 0.16)];
    const bowl = new THREE.Mesh(new THREE.LatheGeometry(prof, 10), iron);
    bowl.position.y = 0.44;
    const embers = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.02, 12), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(0.55) }));
    embers.position.y = 0.57;
    for (const m of [foot, shaft, bowl]) {
      m.castShadow = true;
      g.add(m);
    }
    g.add(embers);
    const flame = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.2, 0.8, 20, 1, true), flameMat(color, core));
    flame.position.y = 0.58 + 0.4;
    flame.renderOrder = 12;
    const inner = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.11, 0.42, 16, 1, true), flameMat(core, core));
    inner.position.y = 0.58 + 0.21;
    inner.renderOrder = 13;
    // A halo over the fire, so it glows from across the bastion.
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: dotTex(), color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0.55 }));
    halo.position.y = 0.95;
    halo.scale.set(0.9, 1.2, 1);
    halo.renderOrder = 11;
    g.add(flame, inner, halo);
    g.position.copy(p);
    this.group.add(g);
    return { g, flame, inner };
  }

  // ---------- every frame ----------

  frame(now: number): void {
    const ease = (cur: number, want: number): number => cur + (want - cur) * 0.18;
    for (const w of this.guards) {
      w.shown = ease(w.shown, w.want);
      const on = w.shown > 0.02;
      w.g.visible = on;
      if (!on) continue;
      (w.shell.material as THREE.ShaderMaterial).uniforms.opacity!.value = w.shown;
      (w.shell.material as THREE.ShaderMaterial).uniforms.time!.value = now;
      (w.sigil.material as THREE.MeshBasicMaterial).opacity = 0.75 * w.shown * (0.85 + 0.15 * Math.sin(now * 4));
      w.sigil.rotation.z = now * 0.5;
      w.shell.rotation.y = now * 0.5;
    }
    for (const f of this.furies) {
      f.shown = ease(f.shown, f.want);
      const on = f.shown > 0.02;
      f.g.visible = on;
      if (!on) continue;
      const beat = 0.75 + 0.25 * Math.pow(Math.abs(Math.sin(now * 3.2)), 3);
      (f.aura.material as THREE.ShaderMaterial).uniforms.opacity!.value = 0.6 * f.shown;
      (f.aura.material as THREE.ShaderMaterial).uniforms.time!.value = now;
      (f.ring.material as THREE.MeshBasicMaterial).opacity = 0.85 * f.shown * beat;
      f.ring.rotation.z = -now * 0.9;
      const s = 0.85 + 0.1 * beat;
      f.ring.scale.set(s, s, s);
    }
    for (const b of this.banners) {
      b.shown = ease(b.shown, b.want);
      const on = b.shown > 0.02;
      b.g.visible = on;
      if (!on) continue;
      const cm = b.cloth.material as THREE.ShaderMaterial;
      cm.uniforms.time!.value = now;
      cm.uniforms.opacity!.value = Math.min(1, b.shown * 1.6);
      b.reach.scale.set(b.reachR, b.reachR, b.reachR);
      b.reach.rotation.z = now * 0.08;
      (b.reach.material as THREE.MeshBasicMaterial).opacity = 0.32 * b.shown * (0.8 + 0.2 * Math.sin(now * 1.6));
    }
    for (const z of this.braziers) {
      for (const m of [z.flame, z.inner]) {
        const u = (m.material as THREE.ShaderMaterial).uniforms;
        u.time!.value = now;
        u.opacity!.value = m === z.flame ? 1.6 : 1.1;
      }
      const flick = 1 + 0.06 * Math.sin(now * 13) + 0.04 * Math.sin(now * 7.3);
      z.flame.scale.set(1, flick, 1);
    }
    const keep: Burst[] = [];
    for (const b of this.bursts) {
      const k = (now - b.t0) / b.life;
      if (k >= 1) {
        b.free();
        continue;
      }
      b.step(Math.max(0, k), b.mesh);
      keep.push(b);
    }
    this.bursts = keep;
    const live: typeof this.wisps = [];
    for (const w of this.wisps) {
      const k = (now - w.t0) / w.life;
      if (k >= 1) {
        w.done();
        continue;
      }
      const e = 1 - (1 - k) * (1 - k);
      const p = w.from.clone().lerp(w.to, e);
      p.y += Math.sin(Math.PI * e) * 0.9;
      w.sprite.position.copy(p);
      const s = 0.42 + 0.12 * Math.sin(now * 30);
      w.sprite.scale.set(s, s, 1);
      (w.sprite.material as THREE.SpriteMaterial).opacity = 1;
      // A trail of fading motes behind the wisp.
      if (this.quality === 'high')
        this.burst(
          'mote',
          () => {
            const s2 = new THREE.Sprite(new THREE.SpriteMaterial({ map: dotTex(), color: '#7fd8ff', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
            s2.renderOrder = 13;
            return s2;
          },
          0.28,
          (() => {
            const at = p.clone();
            return (kk: number, m: THREE.Mesh | THREE.Sprite) => {
              m.position.copy(at);
              const ss = 0.28 * (1 - kk);
              m.scale.set(ss, ss, 1);
              (m.material as THREE.SpriteMaterial).opacity = 0.8 * (1 - kk);
            };
          })(),
        );
      live.push(w);
    }
    this.wisps = live;
  }

  // ---------- makers ----------

  private makeGuard(): Guard {
    const g = new THREE.Group();
    const shell = new THREE.Mesh(
      this.hexGeo,
      shellMat(),
    );
    shell.position.y = 0.62;
    shell.renderOrder = 11;
    const sigil = flat(additive(hexTex(), '#cfe8ff'), 6);
    sigil.visible = true;
    sigil.position.y = 0.04;
    sigil.scale.set(0.72, 0.72, 0.72);
    g.add(shell, sigil);
    g.visible = false;
    this.group.add(g);
    const w: Guard = { g, shell, sigil, shown: 0, want: 0 };
    this.guards.push(w);
    return w;
  }

  private makeFury(): Fury {
    const g = new THREE.Group();
    const aura = new THREE.Mesh(this.coneGeo, flameMat('#ff2a10', '#ff9a50'));
    aura.position.y = 0.72;
    aura.renderOrder = 12;
    const ring = flat(additive(rageTex(), '#ff4f3a'), 6);
    ring.visible = true;
    ring.position.y = 0.035;
    g.add(aura, ring);
    g.visible = false;
    this.group.add(g);
    const f: Fury = { g, aura, ring, shown: 0, want: 0 };
    this.furies.push(f);
    return f;
  }

  private makeBanner(): Banner {
    const g = new THREE.Group();
    const iron = new THREE.MeshStandardMaterial({ color: '#2b2e35', roughness: 0.5, metalness: 0.6 });
    // The pole rises from behind the hero's shoulder; the cloth hangs from a crossbar at its top.
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.022, 1.7, 6), iron);
    pole.position.set(-0.16, 0.85, -0.18);
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.42, 6), iron);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0.02, 1.62, -0.18);
    const finial = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.1, 6), new THREE.MeshStandardMaterial({ color: '#c9ced8', roughness: 0.3, metalness: 0.8 }));
    finial.position.set(-0.16, 1.75, -0.18);
    const clothGeo = new THREE.PlaneGeometry(0.4, 0.6, 10, 8);
    // Hung from its top edge, swinging out sideways from the pole.
    clothGeo.translate(0.2, -0.3, 0);
    const cloth = new THREE.Mesh(
      clothGeo,
      clothMat(),
    );
    cloth.position.set(-0.18, 1.6, -0.18);
    const reach = flat(additive(reachTex(), '#ff6a50'), 5);
    reach.visible = true;
    reach.position.y = 0.035;
    g.add(pole, bar, finial, cloth, reach);
    g.visible = false;
    this.group.add(g);
    const b: Banner = { g, cloth, reach, shown: 0, want: 0, reachR: 1 };
    this.banners.push(b);
    return b;
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
  }
}
